import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import AdUnit from '../components/AdUnit';
import ScrollReveal from '../components/ScrollReveal';
import VerticalSideAd from '../components/VerticalSideAd';
import API_BASE_URL from '../config';

var LIMIT = 10;

function Blog() {
  var [searchParams, setSearchParams] = useSearchParams();
  var page = Number(searchParams.get('page')) || 1;
  var activeTag = searchParams.get('tag') || 'All';
  var [articles, setArticles] = useState([]);
  var [tags, setTags] = useState(['All']);
  var [totalPages, setTotalPages] = useState(1);
  var [loading, setLoading] = useState(true);

  function setPage(p) {
    var params = {};
    if (p > 1) params.page = p;
    if (activeTag !== 'All') params.tag = activeTag;
    setSearchParams(params, { replace: false });
  }

  // Fetch tags once
  useEffect(function () {
    fetch(API_BASE_URL + '/api/articles/tags')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        setTags(['All'].concat(data.tags || []));
      })
      .catch(function () {});
  }, []);

  // Fetch articles when tag or page changes
  useEffect(function () {
    setLoading(true);
    var url = API_BASE_URL + '/api/articles?page=' + page + '&limit=' + LIMIT;
    if (activeTag !== 'All') url += '&tag=' + encodeURIComponent(activeTag);

    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        setArticles(data.articles || []);
        setTotalPages(data.pagination ? data.pagination.totalPages : 1);
        setLoading(false);
      })
      .catch(function () { setLoading(false); });
  }, [activeTag, page]);

  function handleTagChange(tag) {
    setSearchParams(tag !== 'All' ? { tag: tag } : {}, { replace: false });
  }

  var filtered = articles;

  return (
    <div className="page-blog">
      <div className="container">
        <SEO title="Articles — Tech, Business & Security News" description="No clickbait, no pop-ups. Read clear, well-researched articles on technology, business, and cybersecurity." path="/blog" />

        <div className="blog-hero">
          <h1 className="blog-hero-title">Latest Articles</h1>
          <p className="blog-hero-sub">News, insights, and analysis on the topics that matter.</p>
        </div>

        {/* Tag filter pills */}
        <div className="blog-filters">
          {tags.map(function (tag) {
            return (
              <button
                key={tag}
                className={'blog-filter-pill' + (activeTag === tag ? ' blog-filter-pill--active' : '')}
                onClick={function () { handleTagChange(tag); }}
                type="button"
              >
                {tag}
              </button>
            );
          })}
        </div>

        <AdUnit />

        {loading && <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Loading articles…</p>}

        {/* Featured first article */}
        {!loading && filtered.length > 0 && (
          <ScrollReveal>
            <Link to={'/blog/' + filtered[0].slug} className="blog-featured">
              {filtered[0].image && (
                <div className="blog-featured-img-wrap">
                  <img src={filtered[0].image} alt={filtered[0].title} className="blog-featured-img" loading="lazy" />
                  <div className="blog-featured-overlay" />
                </div>
              )}
              <div className="blog-featured-body">
                <div className="blog-featured-meta">
                  <span className="article-tag">{filtered[0].tag}</span>
                  <span className="article-date">{filtered[0].date}</span>
                </div>
                <h2 className="blog-featured-title">{filtered[0].title}</h2>
                <p className="blog-featured-excerpt">{filtered[0].excerpt}</p>
                <span className="blog-featured-cta">Read article →</span>
              </div>
            </Link>
          </ScrollReveal>
        )}

        <AdUnit />

        {/* Rest of articles in a 2-column grid */}
        <div className="blog-grid">
          {filtered.slice(1).map(function (a) {
            return (
              <article className="blog-card" key={a.slug}>
                {a.image && (
                  <Link to={'/blog/' + a.slug} className="blog-card-img-link">
                    <img src={a.image} alt={a.title} className="blog-card-img" loading="lazy" />
                    <div className="blog-card-img-overlay" />
                  </Link>
                )}
                <div className="blog-card-body">
                  <div className="blog-card-meta">
                    <span className="article-tag">{a.tag}</span>
                    <span className="article-date">{a.date}</span>
                  </div>
                  <h2 className="blog-card-title"><Link to={'/blog/' + a.slug}>{a.title}</Link></h2>
                  <p className="blog-card-excerpt">{a.excerpt}</p>
                  <Link to={'/blog/' + a.slug} className="read-more">Read more →</Link>
                </div>
              </article>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', margin: '2rem 0' }}>
            <button
              className="btn btn-secondary"
              disabled={page <= 1}
              onClick={function () { setPage(page - 1); }}
              type="button"
            >← Prev</button>
            <span style={{ padding: '0.5rem 1rem', color: 'var(--text-muted)' }}>
              Page {page} of {totalPages}
            </span>
            <button
              className="btn btn-secondary"
              disabled={page >= totalPages}
              onClick={function () { setPage(page + 1); }}
              type="button"
            >Next →</button>
          </div>
        )}
      </div>
      <VerticalSideAd position="left" />
      <VerticalSideAd position="right" />
    </div>
  );
}

export default Blog;
